import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquareText, Phone, Mail } from "lucide-react";
import { SEO } from "../components/SEO";
import { Reveal } from "../components/Motion";
import { BUSINESS, smsHref } from "../data/site";
import { SERVICES } from "../data/services";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", service: "", location: "", description: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setSent(true);
      toast.success("Quote request sent — we'll be in touch shortly.");
    } catch {
      toast.error("Something went wrong sending that. Please call or text us instead.");
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full bg-cream border border-warmline px-4 py-3 text-ink placeholder:text-smoke/50 focus:border-steel transition-colors";

  return (
    <main data-testid="contact-page">
      <SEO
        title="Contact — Text a Photo for a Fast Quote | Aledo Welding"
        description="Get a welding repair quote in Fort Worth: text photos for the fastest price, call (304) 951-5776, or send the quick-quote form. Serving Tarrant & Parker County."
      />
      <section className="bg-paper grain relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-14 pb-16 md:pt-20">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-steel font-semibold mb-4">Contact & quick quote</p>
            <h1 className="font-display font-black tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl max-w-4xl" data-testid="contact-title">
              Fastest quote wins. That's the text.
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-smoke leading-relaxed">
              Two photos by text gets you the quickest, most accurate price — usually within the hour during business hours. Prefer to talk or type? Those work too.
            </p>
          </Reveal>

          <div className="mt-12 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5 space-y-5">
              <Reveal>
                <a href={smsHref()} data-testid="contact-sms-card" className="group block bg-rust text-cream p-7 btn-cut hover:bg-rust-dark transition-colors">
                  <MessageSquareText size={28} strokeWidth={1.9} aria-hidden />
                  <h2 className="font-display font-extrabold text-2xl tracking-tight mt-4">Text a photo — fast quote</h2>
                  <p className="mt-2 text-cream/85 text-[15px] leading-relaxed">Opens your messages with a template: wide photo, close-up, what broke, address, your name. Fastest way to a price.</p>
                  <span className="mt-4 inline-block font-bold underline underline-offset-4">Start the text</span>
                </a>
              </Reveal>
              <Reveal delay={0.08}>
                <a href={BUSINESS.phoneHref} data-testid="contact-call-card" className="group flex items-center gap-5 bg-charcoal text-cream p-6 btn-cut hover:bg-coal transition-colors">
                  <Phone size={26} strokeWidth={1.9} className="text-gold shrink-0" aria-hidden />
                  <div>
                    <h2 className="font-display font-extrabold text-xl tracking-tight">Call {BUSINESS.phoneDisplay}</h2>
                    <p className="text-cream/70 text-[15px]">{BUSINESS.hours}</p>
                  </div>
                </a>
              </Reveal>
              <Reveal delay={0.16}>
                <a href={`mailto:${BUSINESS.email}`} data-testid="contact-email-card" className="group flex items-center gap-5 border-2 border-ink/60 p-6 btn-cut hover:bg-charcoal hover:text-cream hover:border-charcoal transition-colors">
                  <Mail size={26} strokeWidth={1.9} className="text-steel shrink-0" aria-hidden />
                  <div>
                    <h2 className="font-display font-extrabold text-xl tracking-tight">{BUSINESS.email}</h2>
                    <p className="text-smoke group-hover:text-cream/70 text-[15px]">Drawings, specs & vendor packets welcome</p>
                  </div>
                </a>
              </Reveal>
            </div>

            <div className="lg:col-span-7">
              <Reveal delay={0.1}>
                <div className="bg-sand border border-warmline p-7 sm:p-9" data-testid="quote-form-card">
                  <h2 className="font-display font-extrabold text-2xl tracking-tight">Quick-quote form</h2>
                  <p className="mt-2 text-smoke text-[15px]">For desktop folks who'd rather not text. Same-hour replies during business hours.</p>
                  {sent ? (
                    <div className="mt-8 bg-cream border-2 border-steel p-6" data-testid="quote-success">
                      <h3 className="font-display font-bold text-xl">Got it — quote request received.</h3>
                      <p className="mt-2 text-smoke">We'll reply to {form.phone} shortly. Need it faster? <a href={BUSINESS.phoneHref} className="text-rust font-semibold">Call {BUSINESS.phoneDisplay}</a>.</p>
                    </div>
                  ) : (
                    <form onSubmit={submit} className="mt-7 grid sm:grid-cols-2 gap-5" data-testid="quote-form">
                      <div>
                        <label htmlFor="q-name" className="block text-sm font-bold mb-1.5">Name *</label>
                        <input id="q-name" data-testid="quote-name" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" />
                      </div>
                      <div>
                        <label htmlFor="q-phone" className="block text-sm font-bold mb-1.5">Phone *</label>
                        <input id="q-phone" data-testid="quote-phone" required type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="(817) 555-0000" />
                      </div>
                      <div>
                        <label htmlFor="q-service" className="block text-sm font-bold mb-1.5">What needs fixing?</label>
                        <select id="q-service" data-testid="quote-service" value={form.service} onChange={set("service")} className={inputCls}>
                          <option value="">Not sure / other</option>
                          {SERVICES.map((s) => <option key={s.slug} value={s.name}>{s.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="q-location" className="block text-sm font-bold mb-1.5">Repair location</label>
                        <input id="q-location" data-testid="quote-location" value={form.location} onChange={set("location")} className={inputCls} placeholder="Address or city — or 'I can bring it in'" />
                      </div>
                      <div className="sm:col-span-2">
                        <label htmlFor="q-desc" className="block text-sm font-bold mb-1.5">What happened? *</label>
                        <textarea id="q-desc" data-testid="quote-description" required rows={4} value={form.description} onChange={set("description")} className={inputCls} placeholder="e.g. Driveway gate hinge broke off the post; gate is dragging and won't latch." />
                      </div>
                      <div className="sm:col-span-2">
                        <button type="submit" data-testid="quote-submit" disabled={sending}
                          className="btn-cut bg-rust text-cream font-semibold px-8 py-3.5 hover:bg-rust-dark transition-colors disabled:opacity-60">
                          {sending ? "Sending…" : "Send my quote request"}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
